import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#captureGoogleSearchContext();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    // Load the personalized empty state on first open even when this visitor
    // has no browsing history yet (for example, a first-visit Google Ads click).
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  /**
   * Save Google Ads context for the current browser session.
   * The landing-page product ID is the strongest signal; utm_term is a fallback.
   */
  #captureGoogleSearchContext() {
    try {
      const params = new URLSearchParams(window.location.search);
      const source = (params.get('utm_source') || '').toLowerCase();
      const isGoogle = source === 'google' || params.has('gclid') || params.has('wbraid') || params.has('gbraid');
      if (!isGoogle) return;

      let term = (params.get('utm_term') || '').trim();
      if (/^\{.*\}$/.test(term) || /^(undefined|null)$/i.test(term)) term = '';

      const productId = (this.dataset.currentProductId || '').trim();
      if (!term && !productId) return;

      sessionStorage.setItem('nbss_google_search_context', JSON.stringify({
        term,
        productId,
        savedAt: Date.now(),
      }));
    } catch (error) {
      // Search still works normally when browser storage is blocked.
    }
  }

  #getGoogleSearchContext() {
    try {
      const value = JSON.parse(sessionStorage.getItem('nbss_google_search_context') || 'null');
      if (!value || !value.savedAt || Date.now() - value.savedAt > 24 * 60 * 60 * 1000) {
        sessionStorage.removeItem('nbss_google_search_context');
        return null;
      }
      return value;
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetch a Liquid-rendered row for the campaign landing product or its keyword.
   * Using the theme's search section keeps product cards consistent with Horizon.
   * @returns {Promise<HTMLElement | null>}
   */
  async #getCampaignProductsMarkup() {
    const context = this.#getGoogleSearchContext();
    if (!context || !this.dataset.sectionId) return null;

    let query = '';
    if (context.productId) {
      query = `id:${context.productId}`;
    } else if (context.term) {
      const setNumber = context.term.match(/\b\d{4,6}\b/);
      query = setNumber ? setNumber[0] : context.term;
    }
    if (!query) return null;

    // Use Shopify's predictive-search endpoint (same route as Horizon's native
    // live search), not the full search-results page endpoint. This ensures
    // the predictive-search section receives predictive_search.performed and
    // returns product cards that can be inserted into the empty-state modal.
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const source = parsed.querySelector('#predictive-search-products');
      const sourceList = source?.querySelector('.predictive-search-results__wrapper-products');
      if (!source || !sourceList || !sourceList.children.length) return null;

      const wrapper = document.createElement('div');
      wrapper.className = 'predictive-search-results__products nbss-campaign-products';

      const heading = document.createElement('h4');
      heading.className = 'predictive-search-results__title';
      heading.textContent = 'Recommended for your visit';

      const list = sourceList.cloneNode(true);
      while (list.children.length > 4) list.lastElementChild.remove();
      wrapper.append(heading, list);
      return wrapper;
    } catch (error) {
      console.warn('NOOKBRICK campaign recommendations could not be loaded:', error);
      return null;
    }
  }

  /**
   * Ask Shopify for related products based on the two most recently viewed items.
   * @returns {Promise<object[]>}
   */
  async #getSimilarProducts() {
    const viewedIds = RecentlyViewed.getProducts();
    if (!viewedIds.length) return [];

    const root = window.Shopify?.routes?.root || '/';
    const currency = this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';
    const viewedSet = new Set(viewedIds.map(String));
    const seeds = viewedIds.slice(0, 2);

    const recommendationLists = await Promise.all(seeds.map(async (productId) => {
      const url = new URL(`${root.replace(/\/?$/, '/') }recommendations/products.json`, location.origin);
      url.searchParams.set('product_id', String(productId));
      url.searchParams.set('limit', '6');
      url.searchParams.set('intent', 'related');

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return [];
        const data = await response.json();
        return (data.products || []).map((product) => ({ ...product, _currency: currency }));
      } catch (error) {
        console.warn('NOOKBRICK related products could not be loaded:', error);
        return [];
      }
    }));

    const unique = new Map();
    for (const product of recommendationLists.flat()) {
      const id = String(product.id);
      if (!id || viewedSet.has(id) || product.available === false || unique.has(id)) continue;
      unique.set(id, product);
    }

    return Array.from(unique.values()).slice(0, 4);
  }

  /**
   * Render Shopify recommendation JSON using Horizon's existing resource-card classes.
   * @param {object[]} products
   * @returns {HTMLElement | null}
   */
  #createSimilarProductsMarkup(products) {
    if (!products.length) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'predictive-search-results__products nbss-similar-products';

    const heading = document.createElement('h4');
    heading.className = 'predictive-search-results__title';
    heading.textContent = "Similar to products you've viewed";

    const list = document.createElement('ul');
    list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';

    for (const product of products) {
      const item = document.createElement('li');
      item.className = 'predictive-search-results__card predictive-search-results__card--product';
      item.setAttribute('ref', 'resultsItems[]');

      const card = document.createElement('div');
      card.className = 'resource-card';
      card.dataset.resourceType = 'product';

      const link = document.createElement('a');
      link.className = 'resource-card__link';
      link.href = product.url || `/products/${product.handle}`;
      const hiddenTitle = document.createElement('span');
      hiddenTitle.className = 'visually-hidden';
      hiddenTitle.textContent = product.title || '';
      link.appendChild(hiddenTitle);

      const media = document.createElement('div');
      media.className = 'resource-card__media';
      media.style.setProperty('--resource-card-aspect-ratio', '4 / 5');

      const imageUrl = product.featured_image || (Array.isArray(product.images) ? product.images[0] : '');
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'resource-card__image';
        image.src = imageUrl;
        image.alt = product.title || '';
        image.loading = 'lazy';
        media.appendChild(image);
      }

      const body = document.createElement('div');
      body.className = 'resource-card__content';

      const title = document.createElement('p');
      title.className = 'resource-card__title paragraph';
      title.textContent = product.title || '';
      body.appendChild(title);

      const price = document.createElement('div');
      price.className = 'price';
      const priceText = document.createElement('span');
      priceText.className = 'price-item price-item--regular';
      const currency = product._currency || window.Shopify?.currency?.active || 'USD';
      const amount = Number(product.price || 0) / 100;
      try {
        priceText.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
        }).format(amount);
      } catch (error) {
        priceText.textContent = `${currency} ${amount.toFixed(2)}`;
      }
      price.appendChild(priceText);
      body.appendChild(price);

      card.append(link, media, body);
      item.appendChild(card);
      list.appendChild(item);
    }

    wrapper.append(heading, list);
    return wrapper;
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Horizontal arrows fall through to native text-caret movement, which the
    // browser already mirrors in RTL.
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();
    this.querySelector('.nbss-similar-products')?.remove();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup(excludedIds = []) {
    if (!this.dataset.sectionId) return null;

    const excluded = new Set(excludedIds.map(String));
    const viewedProducts = RecentlyViewed.getProducts().filter((id) => !excluded.has(String(id)));
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    // Start all recommendation requests together instead of waiting for each
    // network request in sequence. This reduces the modal's total wait time.
    const viewedProducts = RecentlyViewed.getProducts();
    const campaignContext = this.#getGoogleSearchContext();
    const excludedCampaignIds = campaignContext?.productId ? [campaignContext.productId] : [];

    const campaignPromise = this.#getCampaignProductsMarkup().catch((error) => {
      console.warn('NOOKBRICK campaign recommendations could not be loaded:', error);
      return null;
    });

    const similarPromise = this.#getSimilarProducts().catch((error) => {
      console.warn('NOOKBRICK similar products could not be loaded:', error);
      return [];
    });

    const recentlyViewedPromise = viewedProducts.length > 0
      ? this.#getRecentlyViewedProductsMarkup(excludedCampaignIds).catch((error) => {
          console.warn('NOOKBRICK recently viewed products could not be loaded:', error);
          return null;
        })
      : Promise.resolve(null);

    // Render Horizon's empty state as soon as its own section request completes.
    // Personalized rows are inserted progressively as their parallel requests finish.
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    if (abortController.signal.aborted) return;

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();

    const collectionElement = predictiveSearchResults.querySelector('#predictive-search-products');
    if (!collectionElement) return;

    // Comment slots preserve the intended row order even when network requests
    // complete at different times: campaign, similar, recently viewed, defaults.
    const defaultContentStart = collectionElement.firstChild;
    const campaignSlot = document.createComment('NOOKBRICK campaign recommendations');
    const similarSlot = document.createComment('NOOKBRICK similar recommendations');
    const recentlyViewedSlot = document.createComment('NOOKBRICK recently viewed');

    collectionElement.insertBefore(campaignSlot, defaultContentStart);
    collectionElement.insertBefore(similarSlot, defaultContentStart);
    if (viewedProducts.length > 0) {
      collectionElement.insertBefore(recentlyViewedSlot, defaultContentStart);
    }

    campaignPromise.then((campaignMarkup) => {
      if (abortController.signal.aborted || !campaignSlot.parentNode) return;
      if (campaignMarkup) campaignSlot.replaceWith(campaignMarkup);
      else campaignSlot.remove();
    });

    similarPromise.then((similarProducts) => {
      if (abortController.signal.aborted || !similarSlot.parentNode) return;
      const similarMarkup = this.#createSimilarProductsMarkup(similarProducts || []);
      if (similarMarkup) similarSlot.replaceWith(similarMarkup);
      else similarSlot.remove();
    });

    recentlyViewedPromise.then((recentlyViewedMarkup) => {
      if (abortController.signal.aborted || !recentlyViewedSlot.parentNode) return;
      if (!recentlyViewedMarkup) {
        recentlyViewedSlot.remove();
        return;
      }

      const parsedRecentlyViewedMarkup = new DOMParser()
        .parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');

      if (!recentlyViewedProductsHtml || !recentlyViewedProductsHtml.children.length) {
        recentlyViewedSlot.remove();
        return;
      }

      const fragment = document.createDocumentFragment();
      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
          fragment.appendChild(child);
        }
      }

      if (fragment.childNodes.length) recentlyViewedSlot.replaceWith(fragment);
      else recentlyViewedSlot.remove();
    });
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
